import { useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { apiGenerateLeadDashReport, apiGenerateDashReport, apiGeneratePolicyDashReport } from 'api';
import type { DashReportFormData } from 'views/dash_report/schemas';

interface UseDashReportOptions {
  leadId?: number | null;
  policyUuid?: string | null;
  onSuccess?: () => void;
  onClose?: () => void;
}

export const useDashReport = (options: UseDashReportOptions = {}) => {
  const { leadId, policyUuid, onSuccess, onClose } = options;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  const generateReport = async (data: DashReportFormData): Promise<void> => {
    setIsSubmitting(true);

    try {
      const reportData = {
        license_number: data.licenseNumber,
        province: data.province,
        dash_requested_years: data.carrier?.dash_requested_years || null,
        pdf_option: 'pdfEnglish',
      };

      let response;

      if (leadId) {
        response = await apiGenerateLeadDashReport(leadId, reportData);
      } else if (policyUuid) {
        response = await apiGeneratePolicyDashReport(policyUuid, reportData);
      } else {
        response = await apiGenerateDashReport(reportData);
      }

      if (!response.success) {
        throw new Error(response.error || 'Failed to generate DASH report');
      }

      // Show success toast
      toast({
        title: 'DASH Report Generated',
        description: leadId
          ? 'DASH report has been generated successfully and attached to this lead.'
          : policyUuid
          ? 'DASH report has been generated successfully and attached to this specialty form.'
          : 'DASH report has been successfully generated and downloaded.',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });

      // Call success callback if provided
      onSuccess?.();

      // Close modal if provided
      onClose?.();
    } catch (error: any) {
      toast({
        title: 'Error Generating DASH Report',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });

      // Re-throw to allow form to handle validation errors if needed
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    generateReport,
    isSubmitting,
  };
};
